import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { errorToastMessage } from "../../../../../utils/toast";
import { goalsApi, Goal } from "../../../../../utils/goalsApi";
import GoalCard from "./GoalCard";
import EditGoalDialog from "./EditGoalDialog";
import DeleteGoalDialog from "./DeleteGoalDialog";
import { DateTime } from "luxon";

const ActiveGoalsList = () => {
    const { id } = useParams();
    const [goals, setGoals] = useState<Goal[]>([]);
    const [loading, setLoading] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
    const [goalToDelete, setGoalToDelete] = useState<{ id: string; title?: string } | null>(null);
    const [toggleLoading, setToggleLoading] = useState(false);
    const [expandedGoalId, setExpandedGoalId] = useState<string | null>(null);
    const [isLoadingProgress, setIsLoadingProgress] = useState(false);
    const [month, setMonth] = useState<string>(DateTime.now().toFormat('yyyy-MM'));
    const [progressData, setProgressData] = useState<Array<{ date: DateTime; status: string; day: number }>>([]);
    const [minDate, setMinDate] = useState<DateTime | null>(null);
    const [maxDate, setMaxDate] = useState<DateTime | null>(null);


    useEffect(() => {
        const fetchGoals = async () => {
            if (!id) return;
            try {
                setLoading(true);
                const data = await goalsApi.getGoals({ participantId: id });
                setGoals(data);
                setExpandedGoalId(data.length > 0 ? data[0].id : null);
            } catch (error) {
                errorToastMessage(error as Error);
            } finally {
                setLoading(false);
            }
        };
        fetchGoals();
    }, [id, toggleLoading]);

    useEffect(() => {
        const fetchProgress = async () => {
            if (!expandedGoalId) return;
            setIsLoadingProgress(true);
            try {
                const startDate = DateTime.fromFormat(month, 'yyyy-MM').startOf('month').toFormat('yyyy-MM-dd');
                const endDate = DateTime.fromFormat(month, 'yyyy-MM').endOf('month').toFormat('yyyy-MM-dd');
                const data = await goalsApi.getGoalProgress(expandedGoalId, startDate, endDate);

                // Calculate heatmap data
                const progressMap = new Map<string, string>();
                if (data && data.length > 0) {
                    data.forEach((p) => {
                        progressMap.set(p.progressDate, p.status);
                    });
                }

                const firstDay = DateTime.fromFormat(month, 'yyyy-MM').startOf('month');
                const lastDay = DateTime.fromFormat(month, 'yyyy-MM').endOf('month');

                const heatmap: Array<{ date: DateTime; status: string; day: number }> = [];
                let currentDate = firstDay;
                while (currentDate <= lastDay) {
                    const dateStr = currentDate.toFormat('yyyy-MM-dd');
                    const status = progressMap.get(dateStr) || 'not_started';

                    heatmap.push({
                        date: currentDate,
                        status: status,
                        day: currentDate.day,
                    });

                    currentDate = currentDate.plus({ days: 1 });
                }

                setProgressData(heatmap);
            } catch (error) {
                errorToastMessage(error as Error);
            } finally {
                setIsLoadingProgress(false);
            }
        };
        fetchProgress();
    }, [expandedGoalId, month]);

    const handleEdit = (goal: Goal) => {
        setSelectedGoal(goal);
        setEditDialogOpen(true);
    };

    const handleDelete = (goalId: string) => {
        const goal = goals.find((g) => g.id === goalId);
        setGoalToDelete({ id: goalId, title: goal?.title });
        setDeleteDialogOpen(true);
    };

    const handleEditSuccess = () => {
        setToggleLoading(prev => !prev);
    };

    const handleDeleteSuccess = () => {
        setToggleLoading(prev => !prev);
    };

    const handleExpand = (goalId: string) => {
        if (expandedGoalId === goalId) {
            setExpandedGoalId(null);
            setMinDate(null);
            setMaxDate(null);
        } else {
            const goal = goals.find(g => g.id === goalId);
            const now = DateTime.now();
            const goalCreatedAt = goal?.createdAt ? DateTime.fromISO(goal.createdAt) : now;
            const calculatedMinDate = DateTime.fromObject({ year: goalCreatedAt.year, month: goalCreatedAt.month, day: 1 });
            const calculatedMaxDate = DateTime.fromObject({ year: now.year, month: now.month, day: 1 });

            setMinDate(calculatedMinDate);
            setMaxDate(calculatedMaxDate);
            setExpandedGoalId(goalId);
            setIsLoadingProgress(true);
        }
        setProgressData([]);
        setMonth(DateTime.now().toFormat('yyyy-MM'));
    }

    const handleMonthChange = (newMonth: string) => {
        setMonth(newMonth);
        setIsLoadingProgress(true);
    }


    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (goals.length === 0) {
        return (
            <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    No goals created yet.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {goals.map((goal) => (
                    <GoalCard
                        key={goal.id}
                        goal={goal}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onExpand={handleExpand}
                        isExpanded={expandedGoalId === goal.id}
                        progressData={progressData}
                        isLoadingProgress={isLoadingProgress}
                        month={month}
                        onMonthChange={handleMonthChange}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                ))}
            </Box>

            {/* Edit Dialog */}
            <EditGoalDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false);
                    setSelectedGoal(null);
                }}
                goal={selectedGoal}
                onSuccess={handleEditSuccess}
            />

            {/* Delete Dialog */}
            <DeleteGoalDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setGoalToDelete(null);
                }}
                goalId={goalToDelete?.id || null}
                goalTitle={goalToDelete?.title}
                onSuccess={handleDeleteSuccess}
            />
        </>
    );
};

export default ActiveGoalsList;
